/**
 * Member ID Input Component
 *
 * Collects insurance member/subscriber ID.
 * Reused for primary, secondary, and retry member ID states.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { Input } from "@frontend/shared/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "../insurance-connector-provider";
import {
  type MemberIdFormData,
  memberIdSchema,
} from "../machine/schemas/validation.schemas";
import { INSURANCE_STEP_FORM_ID } from "../step-config";

export function MemberIdInput() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const state = useInsuranceConnectorState();

  const stateValue = state.value as { full?: string } | string;
  const fullState = typeof stateValue === "object" ? stateValue.full : "";
  const isSecondary = fullState?.includes("secondary") ?? false;
  const isRetry = fullState?.includes("unverifiedRetry") ?? false;
  const insurance = isSecondary
    ? context.secondaryInsurance
    : context.primaryInsurance;

  const form = useForm<MemberIdFormData>({
    resolver: zodResolver(memberIdSchema),
    defaultValues: {
      memberId: insurance?.memberId || "",
    },
  });

  const onSubmit = (data: MemberIdFormData) => {
    if (isSecondary) {
      send({
        type: "SUBMIT",
        data: { secondaryMemberId: data.memberId },
      });
    } else {
      send({ type: "SUBMIT", data });
    }
  };

  const headerText = isRetry
    ? t("ConfirmMemberIdEdit")
    : isSecondary
      ? t("SecondaryMemberIdQuestion")
      : t("PrimaryMemberIdQuestion");

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 md:gap-8"
      >
        <h2 className="text-2xl md:text-[32px] font-semibold leading-[120%]">
          {headerText}
        </h2>
        <FormField
          control={form.control}
          name="memberId"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="sr-only">{t("MemberId")}</FormLabel>
              <FormControl>
                <Input
                  className="rounded-full py-[31px] text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0"
                  placeholder={t("MemberIdPlaceholder")}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
